import { Component } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { arrayOf, bool, func, shape } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { identify } from '@fff-web/fff-analytics';
import { Modal } from '@fff-web/fff-ui-shared';
import { getMembershipInfo } from '@fff-web/fff-utilities';
import withUniversalNavigationContext from '../../hoc/withUniversalNavigationContext';
import { setBoxData } from '../../modules/box';
import { getCampaigns } from '../../modules/campaigns';
import { getUserSubscriptions, unselectProductDetail } from '../../modules/growth';
import { setLayoutData } from '../../modules/layout';
import { setSkipInfo, setTestUserData, setUserData } from '../../modules/user';
import boxStates from '../../utils/boxStates';
import { boxExpired } from '../../utils/boxStatus';
import { campaignState as CAMPAIGN_STATE, getCustomizeOpenStatus } from '../../utils/campaignState';
import {
  getBoxData,
  getCustomizeSaleInfo,
  getSaleProducts,
  getSkipInfo,
  getUserAccountCode,
  getUserAvatar,
  getUserCartData,
  getUserData,
  getUserFirstName,
  saveRevealState,
} from '../../utils/data';
import mapGrowthSubscriptionsToProduct from '../../utils/mapGrowthSubscriptionsToProduct';
import { isClubMember } from '../../utils/user';
import Header from '../Header/Header';
import AddOnsPanel from '../Panels/AddOnsPanel/AddOnsPanel';
import BoxSubscriptionPanel from '../Panels/BoxSubscriptionPanel/BoxSubscriptionPanel';
import CustomizePanel from '../Panels/CustomizePanel/CustomizePanel';
import ExplorePanel from '../Panels/ExplorePanel/ExplorePanel';
import DesktopProductModal from '../Subscriptions/DetailProduct/DesktopProductModal/DesktopProductModal';
import MobileDetailProduct from '../Subscriptions/DetailProduct/MobileDetailProduct/MobileDetailProduct';
import ExpiredUserEmptyState from '../Subscriptions/ExpiredUserEmptyState/ExpiredUserEmptyState';
import Subscriptions from '../Subscriptions/Subscriptions';
import loadingIcon from '../../fff-circle-loading.png';
import * as styles from './MyBoxPage.module.css';

// TODO: How do we handle this for growth subscriptions?
const getSubtotalInDollars = (subscriptions) => {
  if (!subscriptions || !Array.isArray(subscriptions)) return 0;

  return subscriptions.reduce((total, sub) => {
    const price = sub.price ?? sub.retailPrice ?? 0;
    const qty = sub.quantity || 1;
    return total + price * qty;
  }, 0);
};

const CADENCE_TYPE = {
  shipWithBox: 'SWB',
  everyThreeMonths: 'P3M',
};

class Home extends Component {
  state = {
    sysdata: {
      isMobile: this.props.isMobile,
      getBoxData,
      getUserData,
      saveRevealState,
      isProspect: true,
      boxState: '',
      membershipInfo: {},
    },
    boxSubscriptionProducts: {},
    boxSubscriptionSaleInfo: {},
    boxSubscriptionProcessed: false,
    userCartData: [],
  };

  fetchBoxSubscriptionProducts = async (userSubscriptions, countryIsoCode) => {
    const boxSubscriptions = userSubscriptions.filter(
      (sub) => sub.type === 'BOX' && sub.active && !sub.expired && sub.customizeSaleId,
    );

    const productsMap = {};
    const saleInfoMap = {};
    await Promise.all(
      boxSubscriptions.map(async (subscription) => {
        try {
          const [productsData, saleInfoData] = await Promise.all([
            getSaleProducts(subscription.customizeSaleId).catch(() => null),
            getCustomizeSaleInfo(subscription.customizeSaleId, countryIsoCode).catch(() => null),
          ]);

          const { products } = productsData ?? {};
          if (products) {
            const choices = subscription.choices.map((choice) => choice.productUuid);
            productsMap[subscription.id] = products.filter(
              (product) =>
                choices.includes(product.productUuid) ||
                product.variants?.some((variant) => choices.includes(variant.productUuid)),
            );
          }

          if (saleInfoData) {
            saleInfoMap[subscription.id] = saleInfoData;
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          //nothing to do here
        }
      }),
    );

    this.setState({ boxSubscriptionProducts: productsMap, boxSubscriptionSaleInfo: saleInfoMap });
  };

  componentDidMount() {
    this.props.getUserSubscriptions();
    this.props.getCampaigns();
    Promise.all([
      getUserData(),
      getBoxData(),
      getUserFirstName(),
      getUserAvatar(),
      getUserAccountCode(),
      getMembershipInfo(),
      getUserCartData(),
    ])
      .then((values) => {
        const [
          userDataRes,
          boxDataRes,
          userFirstNameRes,
          userAvatarRes,
          userAccountCodeRes,
          membershipInfo,
          userCartInfo,
        ] = values;
        const resUserData = { ...userDataRes, tier: userAccountCodeRes.status };
        this.props.setUserData(resUserData);
        this.props.setBoxData(boxDataRes);
        this.setState({ userCartData: userCartInfo });
        this.setState({
          sysdata: {
            ...this.state.sysdata,
            boxData: boxDataRes,
            userData: resUserData,
            revealCallback: this.revealCallback,
            firstName: userFirstNameRes,
            avatarUrl: userAvatarRes,
            isProspect: userDataRes.success === false,
            membershipInfo,
          },
        });
      })
      .catch((err) => {
        // TODO Show Sorry page or redirect
        console.error('error getting required data ¯\\_(ツ)_/¯', err);
      });

    getSkipInfo().then((res) => this.props.setSkipInfo(res));
    identify();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isMobile !== this.props.isMobile) {
      this.setState({
        sysdata: {
          ...this.state.sysdata,
          isMobile: this.props.isMobile,
        },
      });
    }

    // Wait for user data before fetching customize-sale-info — the endpoint is
    // country-scoped (e.g. beauty box ships to Canada), so we need the user's
    // shipping country to avoid getting US-only data for non-US users.
    const userDataLoaded = 'shippingAddress' in this.props.userData;
    const userDataWasLoaded = 'shippingAddress' in prevProps.userData;
    const subscriptionsChanged = prevProps.userSubscriptions !== this.props.userSubscriptions;
    const userDataJustLoaded = userDataLoaded && !userDataWasLoaded;

    if (userDataLoaded && (subscriptionsChanged || userDataJustLoaded)) {
      const countryIsoCode = this.props.userData.shippingAddress?.country ?? 'US';
      this.fetchBoxSubscriptionProducts(this.props.userSubscriptions, countryIsoCode);
    }
  }

  showExplorePanel = (boxData) => {
    const validStates = [
      boxStates.DELIVERED,
      boxStates.PROB_DELIVERED,
      boxStates.SHIPPED,
      boxStates.PAST_SEASON,
    ];
    return boxData.currentBox && validStates.indexOf(boxData.currentBox.boxStatus) > -1;
  };

  showCustomizePanel = (boxData) => {
    const inValidStates = [
      boxStates.DELIVERED,
      boxStates.PROB_DELIVERED,
      boxStates.SHIPPED,
      boxStates.PAST_SEASON,
    ];
    return !boxData.currentBox || inValidStates.indexOf(boxData.currentBox.boxStatus) < 0;
  };

  // Get the Customize status
  getOpenStatus = (boxData) => getCustomizeOpenStatus(boxData);

  //THIS IS USED FOR REVEAL AND FOR REFRESHING THE CUSTOMIZE TIMER WHEN IT REACHES ZERO
  revealCallback = () => {
    getBoxData().then((boxData) => {
      this.setState({
        sysdata: {
          ...this.state.sysdata,
          boxData,
          // boxData: boxSelfMockData, // Mockdata
        },
      });
    });
  };

  render() {
    const {
      selectedProduct,
      unSelectReplenishment,
      universalNavigationContext,
      userSubscriptions,
    } = this.props;
    const { sysdata } = this.state;
    const { boxData, isMobile, userData, membershipInfo } = sysdata;
    const { hasSkipped, skipEligible } = this.props.userData;
    const isExpired = boxExpired(userData);
    const {
      userSubscriptions: { clubMemberSubscription },
    } = universalNavigationContext;
    const { id: clubMemberSubscriptionId } = clubMemberSubscription ?? {};

    const isClubTier = isClubMember(userData) || !!clubMemberSubscriptionId;
    const isCustomizeOpen = this.getOpenStatus(boxData) === CAMPAIGN_STATE.OPEN;
    const flags = this.props.flags ? this.props.flags : null;
    const showGrowthReactivatePopUp = flags?.growthReactivatePopUp ?? false;
    const { rateMyBoxLinkOnMyBox } = flags;

    const mappedSubscriptions = mapGrowthSubscriptionsToProduct(userSubscriptions);

    const activeMappedSubscriptions = mappedSubscriptions.filter(
      (subscription) => subscription.isActive,
    );

    const goWithBoxSubscriptions = activeMappedSubscriptions.filter(
      (subscription) => subscription.cadenceType === CADENCE_TYPE.shipWithBox,
    );

    const ninetyDaysSubscriptions = activeMappedSubscriptions.filter(
      (subscription) => subscription.cadenceType === CADENCE_TYPE.everyThreeMonths,
    );

    const closeModal = () => unSelectReplenishment();
    const shouldDisplayModal = selectedProduct && Object.keys(selectedProduct).length > 0;
    const isSubscriptionProduct = selectedProduct && 'cadenceType' in selectedProduct;
    const ninetyDaysSubscriptionsQuantity = ninetyDaysSubscriptions
      ? ninetyDaysSubscriptions?.length
      : 0;

    // Blank page when data is not returned
    if (!boxData || !!boxData.code) {
      return (
        <div className="pos-r h-100-vh">
          <img
            alt="Loading"
            data-testid="loading-image"
            className="pos-a page-loading-image"
            src={loadingIcon}
          />
        </div>
      );
    }

    if (shouldDisplayModal && isMobile) {
      // TODO: Get campaign title from API
      const campaignTitle = 'Winter 2025 Customize';

      return (
        <MobileDetailProduct
          subscription={selectedProduct}
          backAction={unSelectReplenishment}
          campaignTitle={campaignTitle}
          isMobile
          subscriptionsQuantity={ninetyDaysSubscriptionsQuantity}
          isRefillSubscription={membershipInfo?.isRefillSubscription}
        />
      );
    }

    const isExploreShown = this.showExplorePanel(boxData) && !membershipInfo?.isRefillSubscription;
    const isCustomizeShown =
      this.showCustomizePanel(boxData) && !membershipInfo?.isRefillSubscription;
    const isCustomizePartialRollout = userData?.flags?.customizePartialRolloutFlag === 'true';
    const showSeasonalBoxSubscriptionPanel = isCustomizePartialRollout && isCustomizeShown;
    const goWithBoxPriceTotal = getSubtotalInDollars(goWithBoxSubscriptions);
    const ninetyDaysPriceTotal = getSubtotalInDollars(ninetyDaysSubscriptions);
    const userBoxSubscriptions = userSubscriptions.filter(
      (subscription) =>
        subscription.type === 'BOX' &&
        subscription.active &&
        !subscription.expired &&
        (subscription.customizeSaleType !== 'seasonal_customize_box' ||
          showSeasonalBoxSubscriptionPanel),
    );

    const userExistingCart =
      Array.isArray(this.state.userCartData) &&
      this.state.userCartData?.find((cart) => !cart.checkedOut);

    return (
      <div className={styles.container}>
        {shouldDisplayModal && !isMobile && isSubscriptionProduct && (
          <Modal isOpen={!!(!isMobile && selectedProduct)} onRequestClose={closeModal}>
            <DesktopProductModal
              onCloseModal={closeModal}
              subscription={selectedProduct}
              subscriptionsQuantity={ninetyDaysSubscriptionsQuantity}
              isRefillSubscription={membershipInfo?.isRefillSubscription}
            />
          </Modal>
        )}
        {(isExpired || isClubTier) && !membershipInfo?.isRefillSubscription ? (
          <ExpiredUserEmptyState
            isExpired={isExpired}
            isClubTier={isClubTier}
            isCustomizeOpen={isCustomizeOpen}
            isCustomizeShown={isCustomizeShown}
            showGrowthReactivatePopUp={showGrowthReactivatePopUp}
            sysdata={sysdata}
            subscriptions={ninetyDaysSubscriptions}
            ninetyDaysPriceTotal={ninetyDaysPriceTotal}
            isShoppingClubUser={!!clubMemberSubscriptionId}
            userBoxSubscriptions={userBoxSubscriptions}
            boxSubscriptionProducts={this.state.boxSubscriptionProducts}
            boxSubscriptionSaleInfo={this.state.boxSubscriptionSaleInfo}
            userExistingCart={userExistingCart}
          />
        ) : (
          <>
            <Header
              sysdata={sysdata}
              hasSkipped={hasSkipped}
              skipEligible={skipEligible}
              showGrowthReactivatePopUp={showGrowthReactivatePopUp}
              rateMyBoxLinkOnMyBoxFlag={rateMyBoxLinkOnMyBox}
            />
            {userBoxSubscriptions.map((userBoxSubscription) => (
              <BoxSubscriptionPanel
                key={userBoxSubscription.id}
                boxSubscription={userBoxSubscription}
                sysdata={sysdata}
                products={this.state.boxSubscriptionProducts[userBoxSubscription.id] || []}
                customizeSaleInfo={this.state.boxSubscriptionSaleInfo[userBoxSubscription.id]}
                userExistingCart={userExistingCart}
              />
            ))}
            {isExploreShown && (
              <ExplorePanel
                sysdata={sysdata}
                boxData={sysdata.boxData}
                winter2020MysteryChoice={flags.winter2020MysteryChoice}
              />
            )}
            <AddOnsPanel sysdata={sysdata} showGrowthReactivatePopUp={showGrowthReactivatePopUp} />
            {isCustomizeShown && !showSeasonalBoxSubscriptionPanel && (
              <CustomizePanel
                sysdata={sysdata}
                showGrowthReactivatePopUp={showGrowthReactivatePopUp}
              />
            )}
            <Subscriptions
              isCustomizeOpen={isCustomizeOpen}
              goWithBoxSubscriptions={goWithBoxSubscriptions}
              goWithBoxPriceTotal={goWithBoxPriceTotal}
              ninetyDaysSubscriptions={ninetyDaysSubscriptions}
              ninetyDaysPriceTotal={ninetyDaysPriceTotal}
              sysdata={sysdata}
              isRefillSubscription={membershipInfo?.isRefillSubscription}
            />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  boxData: state.box.data,
  layout: state.layout,
  userData: state.user,
  isMobile: state.view.isMobile,
  selectedProduct: state.growth.selectedProduct,
  userSubscriptions: state.growth.userSubscriptions,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setBoxData,
      setLayoutData,
      setUserData,
      setTestUserData,
      setSkipInfo,
      getUserSubscriptions,
      getUserCartData,
      getCampaigns,
      unSelectReplenishment: unselectProductDetail,
    },
    dispatch,
  );

Home.propTypes = {
  flags: shape({
    growthReactivatePopUp: bool.isRequired,
  }).isRequired,
  isMobile: bool.isRequired,
  setBoxData: func.isRequired,
  setUserData: func.isRequired,
  userData: shape({
    hasSkipped: bool,
    skipEligible: bool,
    subscriptions: shape({
      live: arrayOf(shape({})),
      expired: arrayOf(shape({})),
    }),
  }).isRequired,
  setSkipInfo: func.isRequired,
  getUserSubscriptions: func.isRequired,
  getCampaigns: func.isRequired,
  selectedProduct: shape({}),
  unSelectReplenishment: func,
  universalNavigationContext: shape({}),
  userSubscriptions: arrayOf(shape({})),
};

Home.defaultProps = {
  selectedProduct: {},
  unSelectReplenishment: () => {},
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(compose(withLDConsumer(), withUniversalNavigationContext)(Home));
